import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getClientProgress,
  getProfessionalProgress,
  getClientReports,
  getProfessionalReports,
  ClientProgressData,
  ProfessionalProgressData,
  ClientReportsData,
  ProfessionalReportsData,
} from "../Actions/UserActions";

interface UserState {
  // Client Progress
  clientProgress: ClientProgressData | null;
  clientProgressLoading: boolean;
  clientProgressError: string | null;

  // Professional Progress
  professionalProgress: ProfessionalProgressData | null;
  professionalProgressLoading: boolean;
  professionalProgressError: string | null;

  // Client Reports
  clientReports: ClientReportsData | null;
  clientReportsLoading: boolean;
  clientReportsError: string | null;

  // Professional Reports
  professionalReports: ProfessionalReportsData | null;
  professionalReportsLoading: boolean;
  professionalReportsError: string | null;
}

const initialState: UserState = {
  // Client Progress
  clientProgress: null,
  clientProgressLoading: false,
  clientProgressError: null,

  // Professional Progress
  professionalProgress: null,
  professionalProgressLoading: false,
  professionalProgressError: null,

  // Client Reports
  clientReports: null,
  clientReportsLoading: false,
  clientReportsError: null,

  // Professional Reports
  professionalReports: null,
  professionalReportsLoading: false,
  professionalReportsError: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // Clear client progress data
    clearClientProgress: (state) => {
      state.clientProgress = null;
      state.clientProgressError = null;
    },

    // Clear professional progress data
    clearProfessionalProgress: (state) => {
      state.professionalProgress = null;
      state.professionalProgressError = null;
    },

    // Clear client reports data
    clearClientReports: (state) => {
      state.clientReports = null;
      state.clientReportsError = null;
    },

    // Clear professional reports data
    clearProfessionalReports: (state) => {
      state.professionalReports = null;
      state.professionalReportsError = null;
    },

    // Clear all user data
    clearAllUserData: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Client Progress
    builder
      .addCase(getClientProgress.pending, (state) => {
        state.clientProgressLoading = true;
        state.clientProgressError = null;
      })
      .addCase(getClientProgress.fulfilled, (state, action: PayloadAction<ClientProgressData>) => {
        state.clientProgressLoading = false;
        state.clientProgress = action.payload;
        state.clientProgressError = null;
      })
      .addCase(getClientProgress.rejected, (state, action) => {
        state.clientProgressLoading = false;
        state.clientProgressError = action.payload as string;
      });

    // Professional Progress
    builder
      .addCase(getProfessionalProgress.pending, (state) => {
        state.professionalProgressLoading = true;
        state.professionalProgressError = null;
      })
      .addCase(getProfessionalProgress.fulfilled, (state, action: PayloadAction<ProfessionalProgressData>) => {
        state.professionalProgressLoading = false;
        state.professionalProgress = action.payload;
        state.professionalProgressError = null;
      })
      .addCase(getProfessionalProgress.rejected, (state, action) => {
        state.professionalProgressLoading = false;
        state.professionalProgressError = action.payload as string;
      });

    // Client Reports
    builder
      .addCase(getClientReports.pending, (state) => {
        state.clientReportsLoading = true;
        state.clientReportsError = null;
      })
      .addCase(getClientReports.fulfilled, (state, action: PayloadAction<ClientReportsData>) => {
        state.clientReportsLoading = false;
        state.clientReports = action.payload;
        state.clientReportsError = null;
      })
      .addCase(getClientReports.rejected, (state, action) => {
        state.clientReportsLoading = false;
        state.clientReportsError = action.payload as string;
      });

    // Professional Reports
    builder
      .addCase(getProfessionalReports.pending, (state) => {
        state.professionalReportsLoading = true;
        state.professionalReportsError = null;
      })
      .addCase(getProfessionalReports.fulfilled, (state, action: PayloadAction<ProfessionalReportsData>) => {
        state.professionalReportsLoading = false;
        state.professionalReports = action.payload;
        state.professionalReportsError = null;
      })
      .addCase(getProfessionalReports.rejected, (state, action) => {
        state.professionalReportsLoading = false;
        state.professionalReportsError = action.payload as string;
      });
  },
});

export const {
  clearClientProgress,
  clearProfessionalProgress,
  clearClientReports,
  clearProfessionalReports,
  clearAllUserData,
} = userSlice.actions;

export default userSlice.reducer;
